import {
  Button,
  DatePickerDropdown,
  Dialog,
  MenuItem,
  Select,
  Text,
  VSpacer,
} from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { EnergyApi } from '@/utilities/api/EnergyApi';
import { ApiEnergyCustomerProgram } from '@api/interfaces/ApiEnergyCustomer';
import { businessToday, formatLocalDate } from '@shared/utilities';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

interface EnergyTierOverrideModalProps {
  energy: ApiEnergyCustomerProgram,
  farmerId: string,
  onClose: () => void,
}

export const EnergyTierOverrideModal = ({
  energy,
  farmerId,
  onClose,
}: EnergyTierOverrideModalProps) => {
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const currentRank = energy.currentTier?.rank ?? null;
  const [rank, setRank] = useState<number | null>(null);
  const today = businessToday();
  const latestDate = energy.programEndDate < today ? energy.programEndDate : today;
  const [effectiveDate, setEffectiveDate] = useState<Date | undefined>(
    () => DateTime.fromISO(latestDate).toJSDate(),
  );
  const [error, setError] = useState('');

  const { isLoading, mutate: overrideTier } = useMutation(
    () => EnergyApi.overrideTier(farmerId, {
      effectiveDate: formatLocalDate(effectiveDate!),
      rank: rank!,
    }),
    {
      onError: (apiError: { message?: string }) => {
        setError(apiError.message || t(($) => $.energy.programModal.errors.generic));
      },
      onSuccess: (updated) => {
        queryClient.setQueryData([QueryKeys.GET_ENERGY_CUSTOMER, farmerId], updated);
        openSnackbar(t(($) => $.energy.profile.overrideSaved));
        onClose();
      },
    },
  );

  const handleSave = () => {
    if (rank === null) {
      setError(t(($) => $.customers.details.tierOverride.errors.tierRequired));
      return;
    }
    if (!effectiveDate) {
      return;
    }
    setError('');
    overrideTier();
  };

  return (
    <Dialog
      acceptButton={(props) => (
        <Button
          {...props}
          disabled={!effectiveDate}
          loading={isLoading}
          onClick={handleSave}
          testID="energy-override-save"
          variant="contained"
        >
          {t(($) => $.customers.details.tierOverride.setOverride)}
        </Button>
      )}
      cancelButton={(props) => (
        <Button {...props} color="inherit" onClick={onClose} testID="energy-override-cancel">
          {t(($) => $.common.actions.cancel)}
        </Button>
      )}
      maxWidth="sm"
      onClose={onClose}
      open
      showCloseButton={false}
      testID="energy-override-modal"
      title={t(($) => $.customers.details.tierOverride.modalTitle)}
    >
      {!!error && (
        <>
          <Text category="body-small" sx={{ color: 'error.main' }} testID="energy-override-error">
            {error}
          </Text>
          <VSpacer size="3" />
        </>
      )}
      <Select
        label={t(($) => $.customers.details.tierOverride.tierFieldLabel)}
        onChangeValue={(value) => setRank(Number(value))}
        testID="energy-override-tier"
        value={rank === null ? '' : String(rank)}
      >
        {energy.tiers.map((tier) => (
          <MenuItem
            disabled={currentRank !== null && tier.rank < currentRank}
            key={tier.rank}
            testID={`energy-override-tier-${tier.rank}`}
            value={String(tier.rank)}
          >
            {tier.name}
          </MenuItem>
        ))}
      </Select>
      <VSpacer size="5" />
      <DatePickerDropdown
        label={t(($) => $.customers.details.tierOverride.dateFieldLabel)}
        maxDate={latestDate}
        minDate={energy.programStartDate}
        onChange={setEffectiveDate}
        testID="energy-override-date"
        value={effectiveDate}
      />
    </Dialog>
  );
};
